/// <amd-module name="Wit/UI/Components/WorkItemGrid/WorkItemGrid.Props" />
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingDataProvider, Wiql, WorkItemId } from "Wit/UI/Data";
/**
 * Interface to work item grid column
 */
export interface IWorkItemGridColumn {
    /**
     * A unique key for identifying the column.
     */
    key: string;
    /**
     * Name to render on the column header.
     */
    name: string;
    /**
     * The field to pull the text value from for the column. This can be null if a custom
     * onRender method is provided.
     */
    fieldName: string;
    /**
     * Minimum width for the column.
     */
    minWidth: number;
    /**
     * Optional maximum width for the column, if stretching is allowed in justified scenarios.
     */
    maxWidth?: number;
    /**
     * Optional accessibility label (aria-label) attribute that will be stamped on to the element.
     * If none is specified, the arai-label attribute will contain the column name
     */
    ariaLabel?: string;
    /**
     * Optional property to determine if the column can be resized.
     */
    isResizable?: boolean;
    /**
     * Optional action to render custom cell content, rather than the default rendering.
     */
    onRender?: (item: WorkItem | null, index?: number, column?: IWorkItemGridColumn) => JSX.Element | null;
}

/**
 * Props for the WorkItemGrid component
 */
export interface IWorkItemGridProps {
    /**
     * Implementation of work item tracking data provider.
     *
     * Component uses it to fetch server resources for rendering.
     */
    dataProvider: IWorkItemTrackingDataProvider;
    /**
     * Items source to provide the items data to render.
     */
    itemsSource: WorkItemId[] | WorkItem[] | Wiql;
    /**
     * Column definitions.
     */
    columns: IWorkItemGridColumn[];
    /**
     * An optional class name to stick on the grid element.
     */
    className?: string;
    /**
     * An optional element for the container of this grid for the intersection
     * to attach to for managing virtualization.
     */
    containerElement?: HTMLElement | string | (() => HTMLElement | null);
    /**
     * Whether to skip measuring for viewport.
     */
    skipViewportMeasures?: boolean;
    /**
     * Whether to disable virtualization while rendering items.
     */
    disableVirtualization?: boolean;
    /**
     * A custom click handler for title link component.
     * To add a default implementation for FastPageSwitching, pass in
     * wit-ui/wit-ui-internal/Behaviors/fastTitleClick.createFastTitleClickHandler, like so:
     * onTitleLinkClick={createFastTitleClickHandler(this.context.pageContext)}
     */
    onTitleLinkClick?: (e: React.MouseEvent<HTMLElement>, workItem: WorkItem) => boolean;
}
